import { hasPermission, PERMISSIONS, type Permission, systemRoleLabel } from '@repo/contracts';
import { AppShell as SharedAppShell } from '@repo/ui';
import {
  Award,
  Building2,
  ClipboardCheck,
  ClipboardList,
  FileBadge,
  GraduationCap,
  Send,
  Settings,
  Users,
} from 'lucide-react';
import { Outlet } from 'react-router';

import { APP_ROUTES } from '@/constants/app-routes';
import { activeAppRoleOf } from '@/hooks/auth/app-roles';
import { useSignOut } from '@/hooks/auth/use-sign-out';
import { SwitchRoleMenu } from '@/shared/components/switch-role/SwitchRoleMenu';
import {
  selectActiveRole,
  selectCurrentUser,
  selectHeldRoles,
  selectPermissions,
} from '@/store/auth.slice';
import { useAppSelector } from '@/store/store';

import { MobileNav } from './MobileNav';

const NAV_ITEMS = [
  {
    to: APP_ROUTES.home,
    label: 'Training',
    icon: GraduationCap,
    end: true,
  },
  {
    to: APP_ROUTES.certificates,
    label: 'My certificates',
    icon: Award,
  },
  {
    to: APP_ROUTES.questionnaires,
    label: 'Questionnaires',
    icon: ClipboardList,
    permission: PERMISSIONS.questionnaireRead,
  },
  {
    to: APP_ROUTES.assignments,
    label: 'Assignments',
    icon: Send,
    permission: PERMISSIONS.trainingAssignmentRead,
  },
  {
    to: APP_ROUTES.review,
    label: 'Review',
    icon: ClipboardCheck,
    permission: PERMISSIONS.trainingAttemptApprove,
  },
  {
    to: APP_ROUTES.allCertificates,
    label: 'Certificates',
    icon: FileBadge,
    permission: PERMISSIONS.certificateRead,
  },
  {
    to: APP_ROUTES.groups,
    label: 'Groups',
    icon: Users,
    permission: PERMISSIONS.trainingGroupRead,
  },
  {
    to: APP_ROUTES.departments,
    label: 'Departments',
    icon: Building2,
    permission: PERMISSIONS.trainingGroupRead,
  },
  {
    to: APP_ROUTES.settings,
    label: 'Settings',
    icon: Settings,
    permission: PERMISSIONS.trainingSettingsRead,
  },
];

export function AppShell() {
  const user = useAppSelector(selectCurrentUser);
  const effectivePermissions = useAppSelector(selectPermissions);
  const activeRole = useAppSelector(selectActiveRole);
  const heldRoles = useAppSelector(selectHeldRoles);
  const signOut = useSignOut();
  const subtitleRole = activeAppRoleOf(heldRoles, activeRole);

  const canAccess = (permission?: string): boolean =>
    !permission || hasPermission(effectivePermissions, [permission as Permission]);

  return (
    <SharedAppShell
      productName="Cyrino"
      sectionName="Training"
      navItems={NAV_ITEMS}
      canAccess={canAccess}
      fullName={user?.fullName}
      subtitle={subtitleRole ? systemRoleLabel(subtitleRole) : undefined}
      onSignOut={signOut}
      accountMenuExtra={<SwitchRoleMenu />}
    >
      <MobileNav onSignOut={signOut} />
      <Outlet />
    </SharedAppShell>
  );
}
